import React, { type FC, type PropsWithChildren, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import type { PartialReadonlyRecord } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType, selectAuthenticatedCapabilities, selectAuthenticatedUsername, Translation } from '../../../domain'

import { useTranslator } from '../Internationalization'
import { useAppState } from '../State'
import type { ReadonlyPropsWithChildren } from '../utils'
import { getRouteCapabilities, getRouteInternalPath } from './Config'
import type { AbsoluteRouteOnlyPath, AbsoluteRoutePath, RouterProps, RouterState } from './Domain'
import { useHasCapabilities } from './Hook.HasCapabilities'
import { ConnectwareRoutingPropsProvider, ConnectwareRoutingProvider, useConnectwareRoutingDispatcher, useConnectwareRoutingProps } from './Router.State'
import { mapState } from './utils'

type RawArgs = PartialReadonlyRecord<Extract<keyof RouterState, 'resourceId' | 'serviceId'>, string>

const DeniedAccess: FC<Readonly<{ path: AbsoluteRoutePath }>> = ({ path }) => {
    const translator = useTranslator()
    const route = getRouteInternalPath(path)
    const capabilitiesAlternatives = getRouteCapabilities(path)
    const username = useAppState(selectAuthenticatedUsername)
    const userCapabilities = useAppState(selectAuthenticatedCapabilities)

    const { deniedAccess: DeniedAccessRenderer } = useConnectwareRoutingProps()

    return (
        <DeniedAccessRenderer
            error={
                new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, translator.formatTranslation(Translation.AUTHENTICATION_ERROR_TITLE), {
                    routeId: path,
                    route,
                    capabilitiesAlternatives,
                    username,
                    userCapabilities,
                })
            }
        />
    )
}

/**
 * This Route
 * - checks and handles permissions of the user
 * - Updates the `ConnectwareRouter` with the up to date id/path
 *      (These props need to be updated so the whole application has the correct routing info without re-rendering all components again)
 */
export const ConnectwareRoute: FC<ReadonlyPropsWithChildren<{ path: AbsoluteRoutePath }>> = ({ path, children }) => {
    const hasRouteCapabilities = useHasCapabilities()
    const dispatch = useConnectwareRoutingDispatcher()

    const { resourceId, serviceId } = useParams<RawArgs>()
    const state = mapState(path, resourceId || null, serviceId || null)

    /**
     * Update the whole CybusRouter
     */
    useEffect(() => dispatch(state), [state.path, state.resourceId, state.serviceId])

    /**
     * Re-declaring the provider ensures that the most up to date information will always be available
     */
    return <ConnectwareRoutingProvider value={state}>{hasRouteCapabilities(path) ? <>{children}</> : <DeniedAccess path={path} />}</ConnectwareRoutingProvider>
}

export const ConnectwareRouter: FC<PropsWithChildren<{ initial: AbsoluteRouteOnlyPath } & RouterProps>> = ({ initial, deniedAccess, children }) => (
    <ConnectwareRoutingPropsProvider value={{ deniedAccess }}>
        <ConnectwareRoutingProvider value={{ path: initial, resourceId: null, serviceId: null }}>{children}</ConnectwareRoutingProvider>
    </ConnectwareRoutingPropsProvider>
)
